import { withNamespaces } from "react-i18next";
import { Row, Col, CardBody, Card, CardTitle } from "reactstrap";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DashboardService from "../../../../../apis/DashboardService";

import Widget from "../../components/Widget";
import { RadialBar, DonutChart, LineChart, BarChart } from "../../components/ApexChart";
import Knob from "../../components/Knob";
import LatestCases from "../../components/LatestCases";
import Filtering from "../../components/Filtering";
import ClosedOnTime from "./components/closed-on-time";
import { toast } from "react-toastify";

const TabDataSubjectContent = (props) => {
    const {
        t
    } = props;


	const [ widgetsList, setWidgetsList ] = useState([]);
	const [ closedOnTimeStats, setClosedOnTimeStats ] = useState(null);
	const [ investigationTime, setInvestigationTime ] = useState(null);
	const [ channelsList, setChannelsList] = useState([]);
	const [ typesByDate , setTypesByDate ] = useState([]);
	const [ categoriesList,  setCategoriesList] = useState([]); 
	const [ casesAssignedToMe, setCasesAssignedToMe ] = useState(null);


	const [filters, setFilters] = useState({});

	const handleFilterOnChange = useCallback((newFilters) => {
		setFilters((currentState) => ({...currentState, ...newFilters}));
	}, []);

    const handleFetchDashboardQuery = useQuery({
		queryKey: ["data-protection-admin-data-subjects-dashboard", filters],
		queryFn: async () => {
		  const service = DashboardService.getInstance();
	
		  return await service.list({
			'createdAt[from]'	:	filters['createdAt[from]'],
			'createdAt[to]'		:	filters['createdAt[to]'],
			questionnaire_name	:	"data_subject_request",
		  });
		},
		cacheTime: 0,
        refetchOnWindowFocus: false,
		onError: (error) => {
		  if (process.env.NODE_ENV === 'development') console.error(error);
	
		  toast(t("An error occurred while fetching dashboard data."), {
			type: "error",
		  });
		}
	});

	useEffect(() => {
		const { data } = handleFetchDashboardQuery;
		if(data){
			const {
				statuses : gotStatuses,
				totalCases,
				investigationTime : gotInvestigationTime,
				sources,
				DPIARisks : gotDPIARisks,
				typesByDate : gotTypesByDate,
				categories : gotCategories,
				riskRate,
				assignedToMe,
				allCountries,
				topCountries,
			} = handleFetchDashboardQuery.data;

			const widgets = [];
			const statuses = [];
			let nextClosedOnTimeStats = null;
			const channels = [];
			const dpiaRisks = [];
			const categories = [];
			const riskRates = [];
			const availableTopCountries = {};

			/**
			 * widgets list
			 */
			widgets.push({
				amount	:	totalCases,
				percent	: 	100,
				title	: 	'Total',
				icon	: 	'ri-stack-line',
				displayChart	: false,
			});
			/**-------------------------- */

			/**
			 * statuses list and widgets list
			 */
			for (const item in gotStatuses) {
				const statusTitle = String(gotStatuses[item]?.title ?? "").trim().toLowerCase();

				statuses.push({
					amount	:	gotStatuses[item].amount,
					percent	:	gotStatuses[item].percent,
					title	:	gotStatuses[item].title,
				});

				if (statusTitle === "closedontime") {
					nextClosedOnTimeStats = gotStatuses[item];
				} else if (!['assigned', 'deleted'].includes(statusTitle)) {
					widgets.push({
						amount	:	gotStatuses[item].amount,
						title	: 	gotStatuses[item].title,
						icon	: 	null,
						chart: {
							show	:	true,
							percent : 	gotStatuses[item].percent
						}
					});
				}
			}
			/**--------------------------- */

			/**
			 * channels list
			 */
			for (const item in (sources || [])) {
				channels.push({
					amount	: 	sources[item].amount,
					title	: 	sources[item].title,
					percent	:	sources[item].percent,
				});
			}
			/**----------------------------- */

			/**
			 * dpia risks 
			 */
			for (const item in (gotDPIARisks || [])) {
				dpiaRisks.push({
					color	: 	gotDPIARisks[item].color,
					amount	: 	gotDPIARisks[item].amount,
					title	: 	gotDPIARisks[item].title,
					percent	:	gotDPIARisks[item].percent,
				});
			}
			/**------------------------------ */

			/**
			 * categories list
			 */
			for (const item in (gotCategories || [])) {
				categories.push({
					amount	:	gotCategories[item].amount,
					title	:	gotCategories[item].title,
					percent	:	gotCategories[item].percent,
				});
			}
			/**------------------------------ */

			/**
			 * risk rates
			 */
			for (const item in (riskRate || [])) {
				riskRates.push({
					color	: 	riskRate[item].color,
					amount	: 	riskRate[item].amount,
					title	: 	riskRate[item].title,
					percent	:	riskRate[item].percent,
				});
			}
			/**-------------------------------- */

			/**
			 * top countires
			 */
			for (const [key, value] of Object.entries(topCountries || [])) {
				if (Number(value) !== 0) {
					availableTopCountries[key] = value;
				}
			}
			/**-------------------------------- */

			setWidgetsList(widgets);
			setClosedOnTimeStats(nextClosedOnTimeStats);
			setInvestigationTime(gotInvestigationTime);
			setChannelsList(channels);
			setTypesByDate(gotTypesByDate);
			setCategoriesList(categories);
			setCasesAssignedToMe(assignedToMe);
		}
	}, [handleFetchDashboardQuery.data]);

    const renderBody = () => {
        if (handleFetchDashboardQuery.isFetching) {
            return (
                <Row className="mt-4">
                    <Col sm='12' md='8'>
                        <Row>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton mt-3"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton mt-3"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                            <Col lg='4'>
                                <div
                                    className="dt-field dt-skeleton mt-3"
                                    style={{ width: '100%', height: '120px' }}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col lg='12'>
                                <div
                                    className="dt-field dt-skeleton mt-3"
                                    style={{ width: '100%', height: '340px' }}
                                />
                            </Col>
                        </Row>
                    </Col>

                    <Col sm='12' md='4'>
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '100%', height: '300px' }}
                        />

                        <div
                            className="dt-field dt-skeleton mt-3"
                            style={{ width: '100%', height: '300px' }}
                        />
                    </Col>
                </Row>
            )
        }

        return (
            <>
                <Row>
                    <Col lg={8}>
                        <Row>
                            {
                                widgetsList.map((widgetData) => {
                                    return (
                                        <Col lg={4} key={widgetData.title}>
                                            <Widget {...widgetData} />
                                        </Col>
                                    )
                                })
                            }

                            {closedOnTimeStats ? (
                                <Col lg={12}>
                                    <ClosedOnTime stats={closedOnTimeStats} />
                                </Col>
                            ) : null}
                        </Row>

                        <Row>
                            <Col lg={12} className="d-flex align-items-stretch">
                                <BarChart title="Processing Time in Days" data={investigationTime} />
                            </Col>
                            <Col lg={12} className="d-flex align-items-stretch">
                                <LineChart title="Amount of DSRs" data={typesByDate} />
                            </Col>
                        </Row>
                    </Col>

                    <Col lg={4}>
                        <DonutChart title={t('Top 5 Categories')} categories={categoriesList} />
                        <RadialBar title={'Channels'} data={channelsList} />

                        <Card className="card_box_Shadow">
                            <CardBody>
                                <div className="mb-5">
                                    <CardTitle>
                                        {t("DSRs Assigned to Me")}
                                    </CardTitle>
                                </div>
                                <div className="text-center" style={{ marginBottom: 84 }}>
                                    <Knob value={casesAssignedToMe?.percent} width={254} height={255} fgColor="#5664d2" lineCap="butt" readOnly={true} thickness={0.2} />
                                </div>
                            </CardBody>
                        </Card>

                    </Col>
                </Row>

                <Row>
                    <Col lg={12}>
                        <LatestCases orgIsAll={false} type={"data_subject_request"} />
                    </Col>
                </Row>
            </>
        )
    }

    return (
        <>
            <Row className="mb-4 mt-4">
                <Col className="d-flex justify-content-end align-items-start">
                    <Filtering onChange={handleFilterOnChange} />
                </Col>
            </Row>

            {renderBody()}
        </>
    );
}

export default withNamespaces()(TabDataSubjectContent);